import { fetchAuthSession } from 'aws-amplify/auth';
import { lambdaEndpoints } from '../aws-config';
import { DataSource, CreateDataSourceInput, BrowseResult } from '../types/dataSource';

const BASE_URL = lambdaEndpoints.dataSources;

class DataSourceService {
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }

  private async request(method: string, path: string = '', body?: any): Promise<any> {
    const token = await this.getAuthToken();
    const url = path ? `${BASE_URL}/${path}` : BASE_URL;

    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || `Request failed: ${res.status}`);
    }
    return data;
  }

  async listSources(): Promise<DataSource[]> {
    const data = await this.request('GET');
    return data.sources || [];
  }

  async createSource(input: CreateDataSourceInput): Promise<DataSource> {
    const data = await this.request('POST', '', input);
    return data.source;
  }

  async updateSource(sourceId: string, input: Partial<CreateDataSourceInput>): Promise<void> {
    await this.request('PUT', sourceId, input);
  }

  async deleteSource(sourceId: string): Promise<void> {
    await this.request('DELETE', sourceId);
  }

  async testSource(sourceId: string): Promise<{ success: boolean; message: string }> {
    return this.request('POST', `${sourceId}/test`);
  }

  async browseSource(sourceId: string, prefix?: string): Promise<BrowseResult> {
    return this.request('POST', `${sourceId}/browse`, { prefix });
  }

  async transferFiles(sourceId: string, files: string[]): Promise<{ count: number }> {
    return this.request('POST', `${sourceId}/transfer`, { files });
  }

  async migrate(): Promise<{ migrated: number }> {
    return this.request('POST', 'migrate');
  }
}

export const dataSourceService = new DataSourceService();
